import InterestDialog, { InterestSuccess } from '../components/InterestDialog';
import { readWorkspace } from '../lib/workspaceStorage';
import { projectOfferStatuses } from '../lib/applicationState';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Users, Wrench, CalendarDays, Clock, Banknote,
  CheckCircle2, ThumbsUp, Building2, ShieldAlert, MinusCircle,
} from 'lucide-react';
import { termState } from '../lib/venueTerms';
import { useAuth } from '../contexts/AuthContext';
import { FeaturedBadge, FounderBadge } from '../components/PlanBadges';
import { TrackRecord, useTrackRecords } from '../components/CooperationTools';
import {
  EVENT_TYPE_LABELS, DAY_LABELS, TIME_RANGE_LABELS, TECH_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;
const labelList = (arr, table) => (arr || []).map(v => table[v] || v);
const money = (n) => new Intl.NumberFormat('tr-TR').format(n || 0);
const priced = (label, n) => n === null || n === undefined ? `${label}: tutar görüşülecek` : `${label}: ₺${money(n)}`;

// A10 (B2): onay kutusu `false` bir "yok" beyanı değildir (form ve DNA bunu sormaz); yalnız `true` iddiadır.
const BoolRow = ({ ok, label, testId }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testId} data-state={ok === true ? 'yes' : 'unknown'}>
    {ok === true
      ? <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-600 shrink-0" />
      : <MinusCircle size={16} strokeWidth={2} className="text-zinc-300 shrink-0" />}
    <span className={ok === true ? 'text-zinc-800' : 'text-zinc-400'}>{ok === true ? label : `${label}: belirtilmemiş`}</span>
  </div>
);

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-white border border-zinc-100 rounded-2xl p-6" data-testid={testId}>
    <h2 className="text-base font-semibold flex items-center gap-2 mb-4 text-[#1E3A8A]">
      <Icon size={17} strokeWidth={1.75} /> {title}
    </h2>
    {children}
  </div>
);

const Gallery = ({ images, name }) => {
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState({});
  if (!images || images.length === 0 || images.every(src => failed[src])) {
    return (
      <div className="w-full h-64 sm:h-80 rounded-2xl bg-[#E6EEFF] flex flex-col items-center justify-center gap-3" data-testid="venue-detail-no-photo">
        <Building2 size={48} strokeWidth={1.25} className="text-[#1E3A8A]/40" />
        <p className="text-sm text-[#1E3A8A]/60">Henüz fotoğraf eklenmemiş</p>
      </div>
    );
  }
  return (
    <div data-testid="venue-detail-gallery">
      <img
        src={images[active]} onError={() => { setFailed(f => ({ ...f, [images[active]]: true })); const next = images.findIndex((src, i) => i !== active && !failed[src]); if (next >= 0) setActive(next); }}
        alt={name}
        className="w-full h-64 sm:h-96 object-cover rounded-2xl bg-zinc-100"
        data-testid="venue-detail-main-photo"
      />
      {images.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`venue-detail-thumb-${i}`}
              className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                i === active ? 'border-[#1E3A8A]' : 'border-transparent hover:border-zinc-300'
              }`}
            >
              <img src={src} alt={`${name} ${i + 1}`} className="w-20 h-14 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VenueDetail = () => {
  const { venueId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [successVenue, setSuccessVenue] = useState(null);
  const [context] = useState(() => location.state?.fromSearch ? readWorkspace(`application-context:${venueId}`) : null);
  const isOrganizer = user?.role === 'organizer';
  // A9: profil detayında yalnız "son 90 günde doğrulanmış N etkinlik" (0 ise gösterilmez).
  const trackRecords = useTrackRecords(isOrganizer && venueId ? [venueId] : []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/venues/${venueId}/detail`);
        setVenue(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Mekân yüklenemedi.');
      }
      if (isOrganizer) {
        try {
          const { data: mine } = await axios.get(`${API}/api/organizer/interests`);
          // A10: tek kanonik kural (sunucunun aynası); sonuçlanan görüşme "talebin var" yerine "sonuçlandı" görünür.
          setApplied(projectOfferStatuses(mine, context?.project_id)[venueId] || false);
        } catch { /* ignore */ }
      }
    })();
  }, [venueId, isOrganizer, context?.project_id]);


  if (error) {
    return (
      <div className="p-10 max-w-2xl mx-auto text-center" data-testid="venue-detail-error">
        <ShieldAlert size={40} strokeWidth={1.25} className="mx-auto text-zinc-300 mb-4" />
        <p className="text-sm text-zinc-600 mb-6">{error}</p>
        <button onClick={() => navigate(-1)} className="text-sm font-medium text-[#1E3A8A] hover:underline">← Geri dön</button>
      </div>
    );
  }
  if (!venue) return <div className="p-8 text-center text-zinc-500">Yükleniyor…</div>;

  // A10 (B2): ortak termState kuralı (VenueTerms ile aynı): bilinmeyen koşul "istemiyor" değil "belirtilmemiş".
  const conditions = [
    ['has_fixed_rent', 'Sabit kira', 'fixed_rent_amount', 'Sabit kira istemiyor'],
    ['has_min_spending', 'Minimum harcama', 'min_spending_amount', 'Minimum harcama şartı yok'],
    ['has_deposit', 'Depozito', 'deposit_amount', 'Depozito istemiyor'],
  ].map(([flag, label, key, none]) => {
    const state = termState(venue, flag);
    return state === true ? { flag, state: 'yes', text: priced(label, venue[key]) }
      : state === false ? { flag, state: 'no', text: none } : { flag, state: 'unknown', text: `${label}: belirtilmemiş` };
  });

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto p-6 sm:p-10">
        <InterestDialog venue={confirming ? venue : null} context={context} onClose={() => setConfirming(false)} onSuccess={v => { setApplied(true); setConfirming(false); setSuccessVenue(v); }} />
        <InterestSuccess venue={successVenue} />
        <button
          onClick={() => navigate(-1)}
          data-testid="venue-detail-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-[#1E3A8A] transition-colors mb-6"
        >
          <ArrowLeft size={16} strokeWidth={1.75} /> Geri
        </button>

        <div className="animate-soft-lift">
          <Gallery key={venueId} images={venue.images} name={venue.name} />
        </div>

        <div className="mt-6 mb-8 animate-soft-lift stagger-1">
          {/* A6: ücretli rozet yalnız aktif ücretli mekânda; kurucu rozeti ayrı (ücretsiz mekân rozet almaz). */}
          {(venue.featured || venue.founder_badge) && <div className="flex flex-wrap gap-1.5 mb-2" data-testid="venue-detail-badges">{venue.featured && <FeaturedBadge />}<FounderBadge founder={venue.founder_badge} /></div>}
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-zinc-900" data-testid="venue-detail-name">
            {venue.name}
          </h1>
          <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1.5" data-testid="venue-detail-location">
            <MapPin size={14} strokeWidth={1.5} className="text-zinc-400" />
            {venue.semt}{venue.mahalle ? ` · ${venue.mahalle}` : ''}{venue.acik_adres ? ` · ${venue.acik_adres}` : ''}
          </p>
          <TrackRecord record={trackRecords[venueId]} />
          {venue.description && (
            <p className="text-base text-zinc-700 mt-4 leading-relaxed" data-testid="venue-detail-description">{venue.description}</p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 animate-soft-lift stagger-2">
          <Section icon={Users} title="Kapasite ve Etkinlikler" testId="venue-detail-capacity">
            <p className="text-2xl font-semibold text-zinc-900 mb-1">
              {venue.min_capacity ? `${venue.min_capacity}–` : 'En fazla '}{venue.max_capacity} <span className="text-sm font-normal text-zinc-500">kişi</span>
            </p>
            <p className="text-xs text-zinc-500 mb-3">{venue.min_capacity_flexible ? 'Minimum esnek; daha küçük gruplar için mekânla teyitleş.' : 'Minimum ve maksimum kişi koşulları geçerlidir.'}</p>
            <div className="flex flex-wrap gap-1.5">
              {labelList(venue.accepted_event_types, EVENT_TYPE_LABELS).map(t => (
                <span key={t} className="text-xs px-2.5 py-1 bg-[#E6EEFF] text-[#1E3A8A] rounded-full font-medium">{t}</span>
              ))}
            </div>
          </Section>

          <Section icon={Banknote} title="Ticari Koşullar" testId="venue-detail-conditions">
            <div className="space-y-2.5">
              {conditions.map(c => (
                <div key={c.flag} className="flex items-center gap-2 text-sm" data-testid={`venue-detail-term-${c.flag}`} data-state={c.state}>
                  {c.state === 'yes'
                    ? <CheckCircle2 size={16} strokeWidth={2} className="text-[#1E3A8A] shrink-0" />
                    : c.state === 'no'
                      ? <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-600 shrink-0" />
                      : <MinusCircle size={16} strokeWidth={2} className="text-zinc-300 shrink-0" />}
                  <span className={c.state === 'unknown' ? 'text-zinc-500' : 'text-zinc-800'}>{c.text}</span>
                </div>
              ))}
              {venue.cancellation_policy && (
                <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-100">
                  <span className="font-semibold">İptal politikası:</span> {venue.cancellation_policy}
                </p>
              )}
            </div>
          </Section>

          <Section icon={CalendarDays} title="Uygun Günler" testId="venue-detail-days">
            <div className="flex flex-wrap gap-1.5 mb-4">
              {labelList(venue.available_days, DAY_LABELS).map(d => (
                <span key={d} className="text-xs px-2.5 py-1 bg-zinc-100 text-zinc-700 rounded-full">{d}</span>
              ))}
              {(venue.available_days || []).length === 0 && <span className="text-sm text-zinc-400">Belirtilmemiş</span>}
            </div>
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Clock size={13} strokeWidth={1.75} /> Saat aralıkları
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {labelList(venue.available_time_ranges, TIME_RANGE_LABELS).map(t => (
                <span key={t} className="text-xs px-2.5 py-1 bg-zinc-100 text-zinc-700 rounded-full">{t}</span>
              ))}
              {(venue.available_time_ranges || []).length === 0 && <span className="text-sm text-zinc-400">Belirtilmemiş</span>}
            </div>
          </Section>

          <Section icon={Wrench} title="Teknik İmkânlar" testId="venue-detail-tech">
            <div className="flex flex-wrap gap-1.5">
              {labelList(venue.technical_amenities, TECH_LABELS).map(t => (
                <span key={t} className="text-xs px-2.5 py-1 bg-zinc-100 text-zinc-700 rounded-full">{t}</span>
              ))}
              {(venue.technical_amenities || []).length === 0 && (venue.technical_absent || []).length === 0 && <span className="text-sm text-zinc-400">Belirtilmemiş</span>}
            </div>
            {/* A10 (B2): "yok" yalnız açık beyandan (technical_absent); listede olmayan imkân bilinmiyor demektir. */}
            {(venue.technical_absent || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="venue-detail-tech-absent">
                {labelList(venue.technical_absent, TECH_LABELS).map(t => (
                  <span key={t} className="text-xs px-2.5 py-1 border border-zinc-200 text-zinc-500 rounded-full">{t} yok</span>
                ))}
              </div>
            )}
          </Section>

          <div className="sm:col-span-2">
            <Section icon={CheckCircle2} title="Mekân Özellikleri" testId="venue-detail-features">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <BoolRow ok={venue.alcohol_allowed} label="Alkol / yemek servisi" testId="feature-alcohol" />
                <BoolRow ok={venue.outside_materials_allowed} label="Dışarıdan malzeme kabul edilir" testId="feature-outside" />
                <BoolRow ok={venue.quiet_event_suitable} label="Sessiz etkinliğe uygun" testId="feature-quiet" />
                <BoolRow ok={venue.table_setup_suitable} label="Masa düzenine uygun" testId="feature-table" />
                <BoolRow ok={venue.has_two_person_table_setup} label="2 kişilik masa düzeni" testId="feature-two-person" />
                <BoolRow ok={venue.min_table_size_60x60_or_more} label="60×60 cm ve üzeri masalar" testId="feature-table-size" />
                <BoolRow ok={venue.has_combinable_tables} label="Birleştirilebilir masalar" testId="feature-combinable" />
                {venue.max_persons_per_table > 0 && (
                  <div className="flex items-center gap-2 text-sm" data-testid="feature-max-per-table">
                    <Users size={16} strokeWidth={2} className="text-[#1E3A8A] shrink-0" />
                    <span className="text-zinc-800">Masa başına en fazla {venue.max_persons_per_table} kişi</span>
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>

        {isOrganizer && (
          <div className="sticky bottom-24 lg:bottom-4 mt-8 animate-soft-lift stagger-3">
            <button
              onClick={() => setConfirming(true)}
              disabled={Boolean(context?.project_id && applied)}
              data-testid="venue-detail-interest-btn"
              className={`w-full py-4 rounded-2xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 active:scale-[0.98] shadow-lg ${
                applied
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 cursor-default shadow-none'
                  : 'bg-[#1E3A8A] hover:bg-[#16307A] text-white'
              } disabled:opacity-60`}
            >
              {applied
                ? (<><CheckCircle2 size={17} strokeWidth={2} /> {applied === 'concluded' ? 'Bu etkinlik için görüşme sonuçlandı' : 'Bu etkinlik için talebin var'}</>)
                : (<><ThumbsUp size={17} strokeWidth={2} /> {'Başvuru gönder'}</>)}
            </button>
            {applied && <Link to="/organizer/applications" className="text-action inline-flex mt-2" data-testid="venue-detail-open-application">{applied === 'concluded' ? 'Görüşmeyi aç →' : 'Mevcut görüşmeyi aç →'}</Link>}
          </div>
        )}

        {user?.role === 'admin' && (
          <p className="mt-8 text-xs text-zinc-400 text-center">
            Admin görünümü — bu mekânı <Link to="/admin/venues" className="text-[#1E3A8A] hover:underline">Mekânlar</Link> panelinden yönetebilirsin.
          </p>
        )}
      </div>
    </div>
  );
};

export default VenueDetail;

